import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { formatCurrency } from "../lib/formatCurrency";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, BookOpen, TrendingUp, TrendingDown, Scale, Trash2, Edit, Download } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { MoreVertical } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INCOME_CATEGORIES = [['grants', 'Grants'], ['donations', 'Donations'], ['contracts', 'Contracts'], ['interest', 'Interest']];
const EXPENSE_CATEGORIES = [
  ['salaries', 'Salaries'], ['printing', 'Printing'], ['travel', 'Travel'], ['accommodation', 'Accommodation'],
  ['fuel', 'Fuel'], ['toll_fees', 'Toll Fees'], ['internet', 'Internet'], ['rentals', 'Rentals'],
  ['equipment', 'Equipment'], ['training_costs', 'Training Costs']
];
const catLabel = (key) => [...INCOME_CATEGORIES, ...EXPENSE_CATEGORIES].find(([k]) => k === key)?.[1]
  || (key === 'bank_and_cash' ? 'Bank & Cash' : key);

const EMPTY_TX = { date: new Date().toISOString().slice(0, 10), type: 'expense', category: 'travel', description: '', amount: '', payment_method: 'bank', reference: '' };

export default function BookkeepingPage() {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [reports, setReports] = useState({});
  const [activeTab, setActiveTab] = useState('transactions');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);

  const [showTxDialog, setShowTxDialog] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [txForm, setTxForm] = useState(EMPTY_TX);

  const rangeParams = useCallback(() => {
    const p = {};
    if (start) p.start = start;
    if (end) p.end = end;
    return p;
  }, [start, end]);

  const loadBase = useCallback(async () => {
    try {
      const [sumRes, txRes] = await Promise.all([
        axios.get(`${API}/bookkeeping/summary`),
        axios.get(`${API}/bookkeeping/transactions`, { params: rangeParams() }),
      ]);
      setSummary(sumRes.data);
      setTransactions(txRes.data);
    } catch (e) {
      toast.error('Failed to load bookkeeping data');
    } finally {
      setLoading(false);
    }
  }, [rangeParams]);

  const loadReport = useCallback(async (tab) => {
    const map = {
      cashbook: 'cashbook', ledger: 'general-ledger', income: 'income-statement',
      balance: 'balance-sheet', trial: 'trial-balance', expenses: 'expenses',
    };
    if (!map[tab]) return;
    try {
      const params = tab === 'balance' ? (end ? { as_of: end } : {}) : rangeParams();
      const res = await axios.get(`${API}/bookkeeping/reports/${map[tab]}`, { params });
      setReports(prev => ({ ...prev, [tab]: res.data }));
    } catch (e) {
      toast.error('Failed to load report');
    }
  }, [rangeParams, end]);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { loadReport(activeTab); }, [activeTab, loadReport]);

  const refreshAll = () => { loadBase(); loadReport(activeTab); };

  const saveTx = async () => {
    if (!txForm.date || !txForm.amount || Number(txForm.amount) <= 0) {
      toast.error('Date and a positive amount are required');
      return;
    }
    try {
      const payload = { ...txForm, amount: Number(txForm.amount), description: txForm.description || null, reference: txForm.reference || null };
      if (editingTx) {
        await axios.put(`${API}/bookkeeping/transactions/${editingTx.id}`, payload);
        toast.success('Transaction updated');
      } else {
        await axios.post(`${API}/bookkeeping/transactions`, payload);
        toast.success('Transaction recorded');
      }
      setShowTxDialog(false);
      setEditingTx(null);
      setTxForm(EMPTY_TX);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save transaction');
    }
  };

  const deleteTx = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await axios.delete(`${API}/bookkeeping/transactions/${id}`);
      toast.success('Transaction deleted');
      refreshAll();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const openEdit = (tx) => {
    setEditingTx(tx);
    setTxForm({ date: tx.date, type: tx.type, category: tx.category, description: tx.description || '', amount: tx.amount, payment_method: tx.payment_method || 'bank', reference: tx.reference || '' });
    setShowTxDialog(true);
  };

  const exportReport = async (fmt) => {
    const map = { cashbook: 'cashbook', ledger: 'general_ledger', income: 'income_statement', balance: 'balance_sheet', trial: 'trial_balance', expenses: 'expenses' };
    const report = map[activeTab];
    if (!report) return;
    try {
      const res = await axios.get(`${API}/bookkeeping/reports/export`, {
        params: { report, format: fmt, ...rangeParams() },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const cats = txForm.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading bookkeeping...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="bookkeeping-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Bookkeeping</h1>
          <p className="text-muted-foreground">Income & expense tracking with auto-generated financial reports</p>
        </div>
        <Button onClick={() => { setEditingTx(null); setTxForm(EMPTY_TX); setShowTxDialog(true); }} data-testid="new-transaction-btn">
          <Plus className="h-4 w-4 mr-2" /> Record Transaction
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Income', value: summary?.total_income, icon: TrendingUp, cls: 'bg-emerald-100 text-emerald-600', testid: 'bk-total-income' },
          { label: 'Total Expenses', value: summary?.total_expenses, icon: TrendingDown, cls: 'bg-rose-100 text-rose-600', testid: 'bk-total-expenses' },
          { label: 'Net Balance', value: summary?.net_balance, icon: Scale, cls: 'bg-black text-[#D4E100]', testid: 'bk-net-balance' },
          { label: 'This Month Net', value: summary?.month_net, icon: BookOpen, cls: 'bg-blue-100 text-blue-600', testid: 'bk-month-net' },
        ].map(k => (
          <Card key={k.label}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`h-12 w-12 rounded-lg ${k.cls} flex items-center justify-center shrink-0`}>
                <k.icon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-lg font-bold font-mono" data-testid={k.testid}>{formatCurrency(k.value || 0)}</p>
                <p className="text-sm text-muted-foreground">{k.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Date range */}
      <div className="flex gap-3 items-end flex-wrap">
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="bk-start-date" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="bk-end-date" />
        </div>
        <Button variant="outline" onClick={refreshAll} data-testid="bk-apply-range">Apply</Button>
        {(start || end) && (
          <Button variant="ghost" onClick={() => { setStart(''); setEnd(''); }} data-testid="bk-clear-range">Clear</Button>
        )}
        {activeTab !== 'transactions' && (
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={() => exportReport('xlsx')} data-testid="bk-export-xlsx">
              <Download className="h-4 w-4 mr-2" /> Excel
            </Button>
            <Button variant="outline" onClick={() => exportReport('pdf')} data-testid="bk-export-pdf">
              <Download className="h-4 w-4 mr-2" /> PDF
            </Button>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto">
          <TabsList>
            <TabsTrigger value="transactions" data-testid="tab-bk-transactions">Transactions</TabsTrigger>
            <TabsTrigger value="cashbook" data-testid="tab-bk-cashbook">Cashbook</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="tab-bk-ledger">General Ledger</TabsTrigger>
            <TabsTrigger value="income" data-testid="tab-bk-income">Income Statement</TabsTrigger>
            <TabsTrigger value="balance" data-testid="tab-bk-balance">Balance Sheet</TabsTrigger>
            <TabsTrigger value="trial" data-testid="tab-bk-trial">Trial Balance</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="tab-bk-expenses">Expense Report</TabsTrigger>
          </TabsList>
        </div>

        {/* Transactions */}
        <TabsContent value="transactions" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map(tx => (
                    <TableRow key={tx.id} data-testid={`tx-row-${tx.id}`}>
                      <TableCell className="text-sm font-mono">{tx.date}</TableCell>
                      <TableCell>
                        <Badge className={tx.type === 'income' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>{tx.type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{catLabel(tx.category)}</TableCell>
                      <TableCell className="text-sm">{tx.description || '-'}</TableCell>
                      <TableCell className="text-sm font-mono">{tx.reference || '-'}</TableCell>
                      <TableCell className="text-sm capitalize">{tx.payment_method}</TableCell>
                      <TableCell className={`text-sm font-mono text-right ${tx.type === 'income' ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`tx-menu-${tx.id}`}><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(tx)}><Edit className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={() => deleteTx(tx.id)}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                  {transactions.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No transactions yet. Record your first income or expense.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cashbook */}
        <TabsContent value="cashbook" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Ref</TableHead>
                    <TableHead className="text-right">Money In</TableHead>
                    <TableHead className="text-right">Money Out</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-muted/40 font-medium">
                    <TableCell colSpan={6}>Opening Balance</TableCell>
                    <TableCell className="text-right font-mono" data-testid="cashbook-opening">{formatCurrency(reports.cashbook?.opening_balance || 0)}</TableCell>
                  </TableRow>
                  {(reports.cashbook?.entries || []).map(e => (
                    <TableRow key={e.id}>
                      <TableCell className="text-sm font-mono">{e.date}</TableCell>
                      <TableCell className="text-sm">{e.description || '-'}</TableCell>
                      <TableCell className="text-sm">{catLabel(e.category)}</TableCell>
                      <TableCell className="text-sm font-mono">{e.reference || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">{e.money_in ? formatCurrency(e.money_in) : ''}</TableCell>
                      <TableCell className="text-right font-mono text-rose-600">{e.money_out ? formatCurrency(e.money_out) : ''}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(e.balance)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/40 font-bold">
                    <TableCell colSpan={6}>Closing Balance</TableCell>
                    <TableCell className="text-right font-mono" data-testid="cashbook-closing">{formatCurrency(reports.cashbook?.closing_balance || 0)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* General Ledger */}
        <TabsContent value="ledger" className="mt-4 space-y-4">
          {(reports.ledger || []).map(acc => (
            <Card key={acc.account} data-testid={`ledger-${acc.account}`}>
              <CardContent className="p-0">
                <div className="flex items-center justify-between px-4 py-2.5 bg-muted/40 flex-wrap gap-2">
                  <p className="font-semibold text-sm">{catLabel(acc.account)} <Badge variant="outline" className="ml-2 capitalize">{acc.type}</Badge></p>
                  <span className="text-xs font-mono text-muted-foreground">Dr {formatCurrency(acc.total_debit)} / Cr {formatCurrency(acc.total_credit)} — Balance {formatCurrency(acc.balance)}</span>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Ref</TableHead>
                        <TableHead className="text-right">Debit</TableHead>
                        <TableHead className="text-right">Credit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {acc.entries.map((e, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm font-mono">{e.date}</TableCell>
                          <TableCell className="text-sm">{e.description || '-'}</TableCell>
                          <TableCell className="text-sm font-mono">{e.reference || '-'}</TableCell>
                          <TableCell className="text-right font-mono">{e.debit ? formatCurrency(e.debit) : ''}</TableCell>
                          <TableCell className="text-right font-mono">{e.credit ? formatCurrency(e.credit) : ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ))}
          {(reports.ledger || []).length === 0 && <p className="text-center text-muted-foreground py-8">No ledger entries for this period.</p>}
        </TabsContent>

        {/* Income Statement */}
        <TabsContent value="income" className="mt-4">
          <Card>
            <CardContent className="p-6 max-w-2xl space-y-4" data-testid="income-statement">
              <div>
                <p className="font-semibold mb-2">Income</p>
                {(reports.income?.income || []).map(r => (
                  <div key={r.category} className="flex justify-between text-sm py-1 border-b border-dashed">
                    <span>{catLabel(r.category)}</span><span className="font-mono">{formatCurrency(r.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold py-1.5"><span>Total Income</span><span className="font-mono text-emerald-600" data-testid="is-total-income">{formatCurrency(reports.income?.total_income || 0)}</span></div>
              </div>
              <div>
                <p className="font-semibold mb-2">Expenses</p>
                {(reports.income?.expenses || []).map(r => (
                  <div key={r.category} className="flex justify-between text-sm py-1 border-b border-dashed">
                    <span>{catLabel(r.category)}</span><span className="font-mono">{formatCurrency(r.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold py-1.5"><span>Total Expenses</span><span className="font-mono text-rose-600" data-testid="is-total-expenses">{formatCurrency(reports.income?.total_expenses || 0)}</span></div>
              </div>
              <div className={`flex justify-between font-bold text-lg border-t-2 pt-3 ${(reports.income?.net_surplus || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                <span>Net {(reports.income?.net_surplus || 0) >= 0 ? 'Surplus' : 'Deficit'}</span>
                <span className="font-mono" data-testid="is-net">{formatCurrency(reports.income?.net_surplus || 0)}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Balance Sheet */}
        <TabsContent value="balance" className="mt-4">
          <Card>
            <CardContent className="p-6 max-w-2xl space-y-4" data-testid="balance-sheet">
              <p className="text-sm text-muted-foreground">As at {reports.balance?.as_of}</p>
              <div>
                <p className="font-semibold mb-2">Assets</p>
                {(reports.balance?.assets || []).map(a => (
                  <div key={a.name} className="flex justify-between text-sm py-1 border-b border-dashed">
                    <span>{a.name}</span><span className="font-mono">{formatCurrency(a.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold py-1.5"><span>Total Assets</span><span className="font-mono" data-testid="bs-total-assets">{formatCurrency(reports.balance?.total_assets || 0)}</span></div>
              </div>
              <div>
                <p className="font-semibold mb-2">Equity & Liabilities</p>
                {(reports.balance?.equity || []).map(e => (
                  <div key={e.name} className="flex justify-between text-sm py-1 border-b border-dashed">
                    <span>{e.name}</span><span className="font-mono">{formatCurrency(e.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold py-1.5"><span>Total Equity & Liabilities</span><span className="font-mono" data-testid="bs-total-equity">{formatCurrency(reports.balance?.total_equity_and_liabilities || 0)}</span></div>
              </div>
              {reports.balance && (
                <Badge className={reports.balance.balanced ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'} data-testid="bs-balanced">
                  {reports.balance.balanced ? '✓ Balanced' : '✗ Out of balance'}
                </Badge>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trial Balance */}
        <TabsContent value="trial" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto" data-testid="trial-balance">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(reports.trial?.rows || []).map(r => (
                    <TableRow key={r.account}>
                      <TableCell className="text-sm">{catLabel(r.account)}</TableCell>
                      <TableCell className="text-sm capitalize">{r.type}</TableCell>
                      <TableCell className="text-right font-mono">{r.debit ? formatCurrency(r.debit) : ''}</TableCell>
                      <TableCell className="text-right font-mono">{r.credit ? formatCurrency(r.credit) : ''}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/40 font-bold">
                    <TableCell colSpan={2}>Totals {reports.trial?.balanced && <Badge className="ml-2 bg-emerald-100 text-emerald-700">✓ Balanced</Badge>}</TableCell>
                    <TableCell className="text-right font-mono" data-testid="tb-total-debit">{formatCurrency(reports.trial?.total_debit || 0)}</TableCell>
                    <TableCell className="text-right font-mono" data-testid="tb-total-credit">{formatCurrency(reports.trial?.total_credit || 0)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expense Report */}
        <TabsContent value="expenses" className="mt-4 space-y-4">
          <Card>
            <CardContent className="p-6" data-testid="expense-report">
              <p className="font-semibold mb-3">Expenses by Category — Total {formatCurrency(reports.expenses?.total || 0)}</p>
              <div className="space-y-2">
                {(reports.expenses?.by_category || []).map(r => (
                  <div key={r.category} className="flex items-center gap-3">
                    <span className="text-sm w-36 shrink-0">{catLabel(r.category)}</span>
                    <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden">
                      <div className="bg-black h-3 rounded-full" style={{ width: `${r.percentage}%` }} />
                    </div>
                    <span className="text-sm font-mono w-28 text-right">{formatCurrency(r.amount)}</span>
                    <span className="text-xs text-muted-foreground w-12 text-right">{r.percentage}%</span>
                  </div>
                ))}
                {(reports.expenses?.by_category || []).length === 0 && <p className="text-muted-foreground text-sm">No expenses in this period.</p>}
              </div>
            </CardContent>
          </Card>
          {(reports.expenses?.by_month || []).length > 0 && (
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Breakdown</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.expenses.by_month.map(m => (
                      <TableRow key={m.month}>
                        <TableCell className="font-mono text-sm">{m.month}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(m.total)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {Object.entries(m.categories).map(([c, a]) => `${catLabel(c)}: ${formatCurrency(a)}`).join(' • ')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Transaction Dialog */}
      <Dialog open={showTxDialog} onOpenChange={setShowTxDialog}>
        <DialogContent className="max-w-lg" data-testid="tx-dialog">
          <DialogHeader>
            <DialogTitle>{editingTx ? 'Edit Transaction' : 'Record Transaction'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={txForm.type} onValueChange={(v) => setTxForm({ ...txForm, type: v, category: v === 'income' ? 'grants' : 'travel' })}>
                  <SelectTrigger data-testid="tx-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={txForm.category} onValueChange={(v) => setTxForm({ ...txForm, category: v })}>
                  <SelectTrigger data-testid="tx-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cats.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={txForm.date} onChange={(e) => setTxForm({ ...txForm, date: e.target.value })} data-testid="tx-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Amount (R)</Label>
                <Input type="number" value={txForm.amount} onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })} placeholder="0.00" data-testid="tx-amount-input" />
              </div>
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={txForm.payment_method} onValueChange={(v) => setTxForm({ ...txForm, payment_method: v })}>
                  <SelectTrigger data-testid="tx-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reference</Label>
                <Input value={txForm.reference} onChange={(e) => setTxForm({ ...txForm, reference: e.target.value })} placeholder="e.g. INV-001" data-testid="tx-reference-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={txForm.description} onChange={(e) => setTxForm({ ...txForm, description: e.target.value })} placeholder="What was this for?" data-testid="tx-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTxDialog(false)} data-testid="tx-cancel-btn">Cancel</Button>
            <Button onClick={saveTx} data-testid="tx-save-btn">{editingTx ? 'Update' : 'Record'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
